import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { fmtPhone } from "../lib/time";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, DollarSign, Loader2, Trash2, MapPin, UserPlus, ChevronDown, Check } from "lucide-react";
import { TableRowsSkeleton } from "../components/skeletons";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function Projects() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const contactIdFromUrl = searchParams.get("contact");
  const shouldCreate = searchParams.get("create") === "true";

  const { 
    projects, 
    fetchProjects, 
    createProject, 
    deleteProject,
    contacts,
    fetchContacts,
    createContact,
    dropdownOptions,
    fetchDropdownOptions,
    pipelineStages,
    fetchPipelineStages,
    pipelineCategories,
    fetchPipelineCategories,
    loading 
  } = useData();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [filterService, setFilterService] = useState("all");
  const [filterStage, setFilterStage] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(contactIdFromUrl || shouldCreate ? true : false);
  const [formData, setFormData] = useState({
    contact_id: contactIdFromUrl || "",
    project_address: "",
    city: "",
    state: "",
    zip: "",
    service_type: "",
    lighting_type: "",
    classification: "retail",
    lead_source: "",
    referral_name: "",
    stage: "hot_lead_inquiry_received",
    notes: "",
    estimated_value: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const [creatingNewContact, setCreatingNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", phone: "", email: "", address: "" });
  const [contactPickerOpen, setContactPickerOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchContacts();
    fetchDropdownOptions();
    fetchPipelineStages();
    fetchPipelineCategories();
  }, [fetchProjects, fetchContacts, fetchDropdownOptions, fetchPipelineStages, fetchPipelineCategories]);

  // ==========================================================================
  // Auto-fill helper — merges contact info into project formData.
  // Only overwrites EMPTY fields so user-typed values are preserved. Used by
  // both the manual contact-select in the modal AND the "?contact=" URL
  // preload path (New Project button on ContactDetail).
  // ==========================================================================
  const applyContactAutofill = useCallback((contact, prev) => {
    const next = { ...prev, contact_id: contact.id };
    // Parse a free-text address into components. Supports the two common shapes:
    //   "123 Main St, Summerville, SC 29483"     (fully comma-separated)
    //   "123 Main St, Summerville SC 29483"      (city+state+zip in one chunk)
    //   "123 Main St Summerville SC 29483"       (no commas at all)
    // Strategy: pull the ZIP off the right, then match against a real US
    // state whitelist so street suffixes like "St", "Dr", "Ave" aren't
    // misidentified as the state. Whatever's left splits into street/city.
    const raw = (contact.address || "").trim();
    let street = "", city = "", state = "", zip = "";
    if (raw) {
      const US_STATES = new Set([
        "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
        "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
        "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR",
      ]);
      let rest = raw;
      const zipMatch = rest.match(/([0-9]{5}(?:-[0-9]{4})?)\s*$/);
      if (zipMatch) {
        zip = zipMatch[1];
        rest = rest.slice(0, zipMatch.index).replace(/[,\s]+$/, "").trim();
      }
      // Only treat trailing 2-letter token as STATE if it's an actual US state code.
      const stateMatch = rest.match(/(?:^|[\s,])([A-Za-z]{2})\s*$/);
      if (stateMatch && US_STATES.has(stateMatch[1].toUpperCase())) {
        state = stateMatch[1].toUpperCase();
        rest = rest.slice(0, stateMatch.index).replace(/[,\s]+$/, "").trim();
      }
      const parts = rest.split(",").map(p => p.trim()).filter(Boolean);
      if (parts.length > 1) {
        city = parts[parts.length - 1];
        street = parts.slice(0, -1).join(", ");
      } else if (parts.length === 1) {
        // No comma at all. Only try to peel a city off if we got either a
        // ZIP or state — otherwise we're likely looking at just a street.
        if (state || zip) {
          const words = parts[0].split(/\s+/);
          // Assume the LAST 1-2 capitalized words are city; if there are 4+
          // words, take the last 2 (handles "Mount Pleasant", "San Diego",
          // "North Charleston"), else take the last 1.
          if (words.length >= 4) {
            city = words.slice(-2).join(" ");
            street = words.slice(0, -2).join(" ");
          } else if (words.length >= 3) {
            city = words[words.length - 1];
            street = words.slice(0, -1).join(" ");
          } else {
            street = parts[0];
          }
        } else {
          street = parts[0];
        }
      }
    }
    if (!next.project_address && street) next.project_address = street;
    if (!next.city && city) next.city = city;
    if (!next.state && state) next.state = state;
    if (!next.zip && zip) next.zip = zip;
    if (!next.service_type && contact.service_type) next.service_type = contact.service_type;
    if (!next.lead_source && contact.lead_source) next.lead_source = contact.lead_source;
    if (!next.classification && contact.classification) next.classification = contact.classification;
    if (!next.referral_name && contact.referral_name) next.referral_name = contact.referral_name;
    return next;
  }, []);

  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setShowCreateModal(true);
    }
    // Preload the contact when opening from ContactDetail's "New Project" button.
    // Waits until contacts have loaded so we can look up the full record.
    const contactParam = searchParams.get("contact");
    if (contactParam && contacts.length > 0) {
      const picked = contacts.find(c => c.id === contactParam);
      if (picked) {
        setShowCreateModal(true);
        setFormData(prev => applyContactAutofill(picked, prev));
      }
    }
    if (searchParams.get("create") === "true" || contactParam) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, contacts, applyContactAutofill]);

  const serviceTypes = dropdownOptions.service_types || [];
  const leadSources = dropdownOptions.lead_sources || [];
  const classifications = dropdownOptions.classifications || [];
  const lightingTypes = dropdownOptions.lighting_types || [];

  const filteredProjects = projects.filter(project => {
    const contact = contacts.find(c => c.id === project.contact_id);
    const matchesSearch = 
      project.project_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesService = filterService === "all" || project.service_type === filterService;
    const matchesStage = filterStage === "all" || project.stage === filterStage;
    
    return matchesSearch && matchesService && matchesStage;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    
    let contactId = formData.contact_id;

    // If creating a new contact inline
    if (creatingNewContact) {
      if (!newContact.name.trim()) { toast.error("Contact name is required"); return; }
      setSubmitting(true);
      try {
        const contactAddress = [newContact.address || formData.project_address, formData.city, formData.state, formData.zip].filter(Boolean).join(", ");
        const created = await createContact({
          name: newContact.name,
          phone: newContact.phone,
          email: newContact.email,
          address: contactAddress,
          service_type: formData.service_type,
          lead_source: formData.lead_source,
          classification: formData.classification,
        });
        contactId = created.id;
        await fetchContacts();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to create contact");
        setSubmitting(false);
        return;
      }
    }

    if (!contactId) {
      toast.error("Please select a contact or create a new one");
      return;
    }
    setSubmitting(true);
    try {
      const fullAddress = [formData.project_address, formData.city, formData.state, formData.zip].filter(Boolean).join(", ");
      const project = await createProject({
        ...formData,
        project_address: fullAddress,
        contact_id: contactId,
        estimated_value: parseFloat(formData.estimated_value) || 0
      });
      toast.success("Project created successfully!");
      setFormData({
        contact_id: "",
        project_address: "",
        city: "",
        state: "",
        zip: "",
        service_type: "",
        classification: "retail",
        lead_source: "",
        referral_name: "",
        stage: "hot_lead_inquiry_received",
        notes: "",
        estimated_value: 0
      });
      setCreatingNewContact(false);
      setNewContact({ name: "", phone: "", email: "", address: "" });
      setSubmitting(false);
      setShowCreateModal(false);
      navigate(`/projects/${project.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create project");
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await deleteProject(id);
        toast.success("Moved to trash");
      } catch (error) {
        toast.error("Failed to delete project");
      }
    }
  };

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact?.name || "Unknown";
  };

  const getStageInfo = (stageValue) => {
    const stage = pipelineStages.find(s => s.value === stageValue);
    return { name: stage?.name || stageValue?.replace(/_/g, ' '), color: stage?.color || "#6B7280" };
  };

  const getServiceColor = (serviceType) => {
    const service = serviceTypes.find(s => s.value === serviceType);
    return service?.color || "#6B7280";
  };

  return (
    <div className="min-h-screen">
      <Header title="Projects">
        <Button
          className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6"
          onClick={() => setShowCreateModal(true)}
          data-testid="create-project-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Project
        </Button>
      </Header>

      <div className="p-4 sm:p-6">
        {/* Service Type Tabs */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center gap-4 overflow-x-auto">
            <div className="flex bg-white border border-slate-200 rounded-lg p-1 gap-0.5 flex-shrink-0" data-testid="project-service-tabs">
              <button
                onClick={() => setFilterService("all")}
                className={`text-sm px-4 py-2 rounded-md font-medium transition-colors whitespace-nowrap ${filterService === "all" ? "bg-[#0F4C81] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid="service-tab-all"
              >All</button>
              {serviceTypes.map(service => (
                <button
                  key={service.value}
                  onClick={() => setFilterService(service.value)}
                  className={`text-sm px-4 py-2 rounded-md font-medium transition-colors whitespace-nowrap ${filterService === service.value ? "bg-[#0F4C81] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`service-tab-${service.value}`}
                >{service.label}</button>
              ))}
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12"
                data-testid="search-projects"
              />
            </div>
            <Select value={filterStage} onValueChange={setFilterStage}>
              <SelectTrigger className="w-full md:w-48 h-12">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {pipelineCategories.map(cat => {
                  const catStages = pipelineStages.filter(s => s.category === cat.value);
                  if (!catStages.length) return null;
                  return (
                    <div key={cat.value}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase">{cat.name}</div>
                      {catStages.map(s => <SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>)}
                    </div>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Projects Table */}
        {loading ? (
          <TableRowsSkeleton rows={6} cols={5} />
        ) : filteredProjects.length > 0 ? (
          <>
            {/* Mobile: stacked cards so nothing horizontally scrolls */}
            <div className="sm:hidden space-y-2" data-testid="projects-mobile-list">
              {filteredProjects.map((project) => {
                const stageInfo = getStageInfo(project.stage);
                return (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => navigate(`/projects/${project.id}`, { state: { from: "/projects" } })}
                    className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 active:bg-slate-50"
                    data-testid={`project-card-${project.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 truncate">{getContactName(project.contact_id)}</p>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{project.project_address || "No address"}</span>
                        </div>
                      </div>
                      <Badge
                        style={{ backgroundColor: `${stageInfo.color}20`, color: stageInfo.color }}
                        className="flex-shrink-0"
                      >
                        {stageInfo.name}
                      </Badge>
                    </div>
                    {project.service_type && (
                      <div className="mt-2">
                        <Badge
                          style={{
                            backgroundColor: `${getServiceColor(project.service_type)}20`,
                            color: getServiceColor(project.service_type),
                          }}
                          className="text-xs"
                        >
                          {serviceTypes.find(s => s.value === project.service_type)?.label || project.service_type}
                        </Badge>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Contact</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Address</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Service</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Stage</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => {
                  const stageInfo = getStageInfo(project.stage);
                  return (
                    <tr 
                      key={project.id}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/projects/${project.id}`, { state: { from: "/projects" } })}
                      data-testid={`project-row-${project.id}`}
                    >
                      <td className="px-4 py-4">
                        <span className="font-medium text-slate-800">
                          {getContactName(project.contact_id)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-slate-600">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="truncate max-w-[200px]">
                            {project.project_address || "No address"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {project.service_type && (
                          <Badge 
                            style={{ 
                              backgroundColor: `${getServiceColor(project.service_type)}20`,
                              color: getServiceColor(project.service_type)
                            }}
                          >
                            {serviceTypes.find(s => s.value === project.service_type)?.label || project.service_type}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <Badge 
                          style={{ 
                            backgroundColor: `${stageInfo.color}20`,
                            color: stageInfo.color
                          }}
                        >
                          {stageInfo.name}
                        </Badge>
                      </td>
                      <td className="px-4 py-4">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={(e) => handleDelete(project.id, e)}
                          data-testid={`delete-project-${project.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {searchQuery || filterService !== "all" || filterStage !== "all" 
                ? "No projects found" 
                : "No projects yet"}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchQuery || filterService !== "all" || filterStage !== "all"
                ? "Try adjusting your filters" 
                : "Create your first project to get started"}
            </p>
            <Button
              className="bg-[#0F4C81] hover:bg-[#0D4270]"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-5 h-5 mr-2" />
              New Project
            </Button>
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Add a new project for a contact
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Contact *</Label>
                <button
                  type="button"
                  className="text-xs text-[#0F4C81] hover:underline flex items-center gap-1"
                  onClick={() => { setCreatingNewContact(!creatingNewContact); setFormData(f => ({ ...f, contact_id: "" })); }}
                  data-testid="toggle-new-contact"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {creatingNewContact ? "Select Existing" : "Create New Contact"}
                </button>
              </div>

              {creatingNewContact ? (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <Label className="text-xs">Name *</Label>
                    <Input
                      value={newContact.name}
                      onChange={e => setNewContact({ ...newContact, name: e.target.value })}
                      placeholder="Full name"
                      className="h-10 mt-1"
                      data-testid="new-contact-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Phone</Label>
                      <Input
                        value={newContact.phone}
                        onChange={e => setNewContact({ ...newContact, phone: fmtPhone(e.target.value) })}
                        placeholder="(843) 555-1234"
                        className="h-10 mt-1"
                        data-testid="new-contact-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input
                        value={newContact.email}
                        onChange={e => setNewContact({ ...newContact, email: e.target.value })}
                        placeholder="email@example.com"
                        className="h-10 mt-1"
                        data-testid="new-contact-email"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <Popover open={contactPickerOpen} onOpenChange={setContactPickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={contactPickerOpen}
                      className="flex w-full items-center justify-between h-12 rounded-md border border-slate-200 bg-white px-3 text-sm text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                      data-testid="project-contact-select"
                    >
                      {(() => {
                        const picked = contacts.find(c => c.id === formData.contact_id);
                        if (!picked) return <span className="text-slate-400">Search & pick a contact...</span>;
                        return (
                          <span className="truncate text-slate-700">
                            {picked.name}{picked.phone ? ` — ${fmtPhone(picked.phone)}` : ""}
                          </span>
                        );
                      })()}
                      <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command
                      filter={(value, search) => {
                        // value is what we pass on CommandItem; case-insensitive substring match.
                        return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                      }}
                    >
                      <CommandInput placeholder="Search by name, phone, or email..." data-testid="project-contact-search" />
                      <CommandList>
                        <CommandEmpty>No matching contacts</CommandEmpty>
                        <CommandGroup>
                          {contacts.map(contact => (
                            <CommandItem
                              key={contact.id}
                              value={`${contact.name || ""} ${contact.phone || ""} ${contact.email || ""}`.trim()}
                              onSelect={() => {
                                setFormData(prev => applyContactAutofill(contact, prev));
                                setContactPickerOpen(false);
                              }}
                              className="flex items-center justify-between gap-2 py-2"
                              data-testid={`project-contact-option-${contact.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{contact.name || "(no name)"}</p>
                                {(contact.phone || contact.email) && (
                                  <p className="text-[11px] text-slate-400 truncate">
                                    {contact.phone && fmtPhone(contact.phone)}
                                    {contact.phone && contact.email && " · "}
                                    {contact.email}
                                  </p>
                                )}
                              </div>
                              {formData.contact_id === contact.id && (
                                <Check className="w-4 h-4 text-[#0F4C81] shrink-0" />
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Project Street Address</Label>
              <Input
                id="address"
                value={formData.project_address}
                onChange={(e) => setFormData({ ...formData, project_address: e.target.value })}
                placeholder="123 Main Street"
                className="h-12"
                data-testid="project-address-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={formData.city || ""}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Charleston"
                  className="h-12"
                  data-testid="project-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={formData.state || ""}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="SC"
                  className="h-12"
                  data-testid="project-state-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Zip</Label>
                <Input
                  value={formData.zip || ""}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  placeholder="29401"
                  className="h-12"
                  data-testid="project-zip-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Service Type *</Label>
                <Select 
                  value={formData.service_type} 
                  onValueChange={(v) => setFormData({ ...formData, service_type: v, lighting_type: v === "exterior_lighting" ? formData.lighting_type : "" })}
                >
                  <SelectTrigger className="h-12" data-testid="project-service-select">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map(service => (
                      <SelectItem key={service.value} value={service.value}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lead Source</Label>
                <Select 
                  value={formData.lead_source} 
                  onValueChange={(v) => setFormData({ ...formData, lead_source: v })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadSources.map(source => (
                      <SelectItem key={source.value} value={source.value}>
                        {source.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.service_type === "exterior_lighting" && (
              <div className="space-y-2" data-testid="lighting-type-field">
                <Label>Lighting Type</Label>
                <Select
                  value={formData.lighting_type || ""}
                  onValueChange={(v) => setFormData({ ...formData, lighting_type: v })}
                >
                  <SelectTrigger className="h-12" data-testid="project-lighting-type-select">
                    <SelectValue placeholder="Christmas or Govee?" />
                  </SelectTrigger>
                  <SelectContent>
                    {(lightingTypes || []).map(l => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Referral Name</Label>
              <Input
                value={formData.referral_name}
                onChange={(e) => setFormData({ ...formData, referral_name: e.target.value })}
                placeholder="Who referred this lead? (so we know who to thank)"
                className="h-12"
                data-testid="project-referral-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Classification</Label>
                <Select 
                  value={formData.classification} 
                  onValueChange={(v) => setFormData({ ...formData, classification: v })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {classifications.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="value">Estimated Value</Label>
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><Input
                  id="value"
                  type="number"
                  value={formData.estimated_value}
                  onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })}
                  placeholder="0"
                  className="h-12 pl-7"
                  data-testid="project-value-input"
                /></div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Project notes..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-12"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 bg-[#0F4C81] hover:bg-[#0D4270]"
                disabled={submitting}
                data-testid="submit-project-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Project"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
